import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useMsal, useIsAuthenticated, useAccount } from '@azure/msal-react';
import { InteractionStatus, InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiScopes } from '../msalConfig';
import { logIn, logOut } from '../redux/authSlice';
import { UserType } from '../constants';

const AuthContext = createContext();

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
};

async function acquireToken(instance, account) {
    try {
        const result = await instance.acquireTokenSilent({ scopes: apiScopes, account });
        return result.accessToken;
    } catch (e) {
        if (e instanceof InteractionRequiredAuthError) {
            await instance.acquireTokenRedirect({ scopes: apiScopes, account });
            return null;
        }
        throw e;
    }
}

export const AuthProvider = ({ children }) => {
    const dispatch = useDispatch();
    const { instance, accounts, inProgress } = useMsal();
    const isMsalAuthenticated = useIsAuthenticated();
    const account = useAccount(accounts[0] || null);
    const [authzState, setAuthzState] = useState(null); // null | { ok: boolean, error?: { code, requiredRole } }

    const isLoading = inProgress !== InteractionStatus.None
        || (isMsalAuthenticated && authzState === null);

    useEffect(() => {
        if (!isMsalAuthenticated || !account) {
            setAuthzState(null);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const token = await acquireToken(instance, account);
                if (!token) return;
                const res = await fetch(
                    `${window.AnalyticsPortalConfig.apiAddress}/PortalSessions/validation`,
                    { headers: { 'Authorization': `Bearer ${token}` } }
                );
                if (cancelled) return;
                if (res.ok) {
                    setAuthzState({ ok: true });
                    dispatch(logIn({ username: account.username, userType: UserType.SLAUser }));
                } else {
                    const body = await res.json().catch(() => ({}));
                    setAuthzState({
                        ok: false,
                        error: {
                            code: body.error || 'forbidden',
                            requiredRole: body.requiredRole ?? null,
                        },
                    });
                    dispatch(logOut());
                }
            } catch (e) {
                console.error('Authz check failed:', e);
                if (!cancelled) {
                    setAuthzState({ ok: false, error: { code: 'network', requiredRole: null } });
                }
            }
        })();
        return () => { cancelled = true; };
    }, [isMsalAuthenticated, account, instance, dispatch]);

    const login = useCallback(() => {
        if (inProgress !== InteractionStatus.None) return undefined;
        return instance.loginRedirect({ scopes: apiScopes });
    }, [instance, inProgress]);

    const logout = useCallback(() => {
        dispatch(logOut());
        return instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
    }, [instance, dispatch]);

    return (
        <AuthContext.Provider value={{
            isMsalAuthenticated,
            isLoading,
            authzError: authzState?.error ?? null,
            username: account?.username ?? null,
            login,
            logout,
        }}>
            {children}
        </AuthContext.Provider>
    );
};
